<script setup>
// Catálogo de servicios del pedido (envío o paquetería, impresiones extra):
// conceptos que se cobran pero no son producto físico, así que no llevan
// inventario. Reemplaza al antiguo catálogo de Artículos de Ventas; los
// productos físicos ahora viven en el catálogo de Productos (CP&L).
import { ref, computed, watch, onMounted } from "vue";
import {
  getServicios,
  crearServicio,
  actualizarServicio,
  eliminarServicio,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionServiciosStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

// ── Store compartido ────────────────────────────────────────
const paginacionStore = usePaginacionServiciosStore();

// ── Estado ─────────────────────────────────────────────────
const servicios = ref([]);
const filtro = ref("");
const loading = ref(false);
const paginaActual = ref(1);
const guardando = ref(false);
const editando = ref(false);
const modalElement = ref(null);
const form = ref({
  id: null,
  codigo: "",
  nombre: "",
});

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("codigo");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

let modalInstance = null;

// Resetear página al filtrar
watch(filtro, () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const serviciosFiltrados = computed(() => {
  const s = filtro.value.toLowerCase();
  return servicios.value.filter(
    (a) =>
      a.codigo.toLowerCase().includes(s) ||
      (a.nombre && a.nombre.toLowerCase().includes(s))
  );
});

const serviciosOrdenados = computed(() => {
  return [...serviciosFiltrados.value].sort((a, b) => {
    let valA = a[sortKey.value] ?? "";
    let valB = b[sortKey.value] ?? "";
    const cmp = String(valA).localeCompare(String(valB), "es", { sensitivity: "base" });
    return sortDir.value === "asc" ? cmp : -cmp;
  });
});

const porPagina = computed(() => paginacionStore.porPagina);

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(serviciosOrdenados.value.length / porPagina.value))
);

const serviciosPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return serviciosOrdenados.value.slice(inicio, inicio + porPagina.value);
});

const rangoInicio = computed(() =>
  serviciosOrdenados.value.length === 0
    ? 0
    : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, serviciosOrdenados.value.length)
);

const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

// ── Métodos ────────────────────────────────────────────────
function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

async function cargarServicios() {
  loading.value = true;
  try {
    servicios.value = await getServicios();
  } catch {
    Swal.fire("Error", "No se pudieron obtener los servicios", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(servicio = null) {
  if (servicio) {
    editando.value = true;
    form.value = {
      id: servicio.id,
      codigo: servicio.codigo,
      nombre: servicio.nombre || "",
    };
  } else {
    editando.value = false;
    form.value = {
      id: null,
      codigo: "",
      nombre: "",
    };
  }
  modalInstance.show();
}

// Cierra el modal y espera su animación, para que el aviso de éxito no
// aparezca encimado sobre él.
function ocultarModal() {
  return new Promise((resolve) => {
    modalElement.value.addEventListener("hidden.bs.modal", () => resolve(), { once: true });
    modalInstance.hide();
  });
}

async function guardarServicio() {
  guardando.value = true;
  try {
    const payload = {
      codigo: form.value.codigo.trim(),
      nombre: form.value.nombre.trim(),
    };
    if (editando.value) {
      await actualizarServicio(form.value.id, payload);
    } else {
      await crearServicio(payload);
    }
    await cargarServicios();
    await ocultarModal();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Servicio ${editando.value ? "actualizado" : "registrado"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (e) {
    const detalle = e?.response?.data;
    const msg = detalle?.codigo?.[0] || detalle?.nombre?.[0] || detalle?.detail;
    Swal.fire("Error", msg || "No se pudo guardar el servicio", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(servicio) {
  const res = await Swal.fire({
    title: `¿Desactivar ${servicio.codigo}?`,
    text: "El servicio dejará de aparecer al armar pedidos.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarServicio(servicio.id);
      await cargarServicios();
      Swal.fire({
        icon: "success",
        title: "Listo",
        text: "Servicio desactivado",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar el servicio", "error");
    }
  }
}

onMounted(() => {
  cargarServicios();
  modalInstance = new Modal(modalElement.value);
});
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Servicios"
      v-model="filtro"
      searchPlaceholder="Buscar por código o nombre..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-plus-lg"></i>
          <span class="btn-header-texto ms-2">Nuevo servicio</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="codigo"
                label="Código"
                :filterable="false"
                :items="serviciosFiltrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="nombre"
                label="Nombre"
                :filterable="false"
                :items="serviciosFiltrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="servicio in serviciosPaginados" :key="servicio.id">
            <td class="ps-4">
              <span class="badge-molde">{{ servicio.codigo }}</span>
            </td>
            <td class="fw-bold text-cafe">{{ servicio.nombre || "—" }}</td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(servicio)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorrado(servicio)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="serviciosPaginados.length === 0 && !loading">
            <td colspan="3" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron servicios
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info">
            {{ rangoInicio }}–{{ rangoFin }} de {{ serviciosOrdenados.length }} servicios
          </small>
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>

        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>

          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>

          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="servicio in serviciosPaginados"
        :key="'mob-' + servicio.id"
        class="divisan-mobile-card mb-2 px-3 py-2 shadow-sm"
      >
        <!-- Fila 1: código + botones -->
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="badge-molde">{{ servicio.codigo }}</span>
          <div class="d-flex gap-2">
            <button class="btn-circular-sm" @click="abrirModal(servicio)" title="Editar">
              <i class="bi bi-pencil-square text-verde"></i>
            </button>
            <button
              class="btn-circular-sm"
              @click="confirmarBorrado(servicio)"
              title="Desactivar"
            >
              <i class="bi bi-trash3-fill text-danger"></i>
            </button>
          </div>
        </div>
        <!-- Fila 2: nombre -->
        <div class="descripcion-mobile">{{ servicio.nombre || "—" }}</div>
      </div>

      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info">
            {{ rangoInicio }}–{{ rangoFin }} de {{ serviciosOrdenados.length }} servicios
          </small>
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered modal-md">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar servicio" : "Nuevo servicio" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardarServicio">
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-12 col-md-5">
                  <label class="form-label label-divisan">Código</label>
                  <input
                    v-model="form.codigo"
                    type="text"
                    class="form-control divisan-input-modal"
                    required
                    placeholder="Ej. ENV"
                  />
                </div>
                <div class="col-12 col-md-7">
                  <label class="form-label label-divisan">Nombre</label>
                  <input
                    v-model="form.nombre"
                    type="text"
                    class="form-control divisan-input-modal"
                    required
                    placeholder="Ej. Envío"
                  />
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar servicio" : "Guardar servicio" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}

.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper {
  position: relative;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
}
.search-wrapper input {
  padding-left: 35px;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}

.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}

.badge-molde {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
  padding: 4px 10px;
  border-radius: 6px;
  font-family: var(--d-font-mono);
  font-size: 0.78rem;
  font-weight: 600;
}
.badge-color {
  background-color: rgba(96, 108, 56, 0.12);
  color: var(--d-green-dark);
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(96, 108, 56, 0.25);
}
.badge-bool {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}
.badge-bool.si {
  background-color: #dda15e;
  color: var(--d-brown);
}
.badge-bool.no {
  background-color: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  border: 1px solid rgba(64, 34, 8, 0.15);
}
.badge-estado {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}
.activo {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.inactivo {
  background-color: var(--d-brown);
  color: var(--d-cream);
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}
.switch-divisan {
  width: 2.5em;
  height: 1.4em;
  cursor: pointer;
}
.switch-divisan:checked {
  background-color: var(--d-green);
  border-color: var(--d-green);
}

/* ── Paginación escritorio ── */
.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}

/* Select por página */
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}

/* Botones de navegación */
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}

.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}

.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

/* ── Paginación móvil ── */
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

.descripcion-mobile {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--d-green);
  line-height: 1.3;
}
.btn-circular-sm {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.8rem;
}
.btn-circular-sm:hover {
  background-color: rgba(96, 108, 56, 0.1);
}

.btn-nuevo-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.btn-exportar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-exportar:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .btn-nuevo-texto,
  .btn-header-texto {
    display: none;
  }
  .btn-nuevo-header,
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
